import { isPastDate } from 'src/utils/CommonFunctions';

/** Normalizes date strings from pickers (MM-DD-YYYY, YYYY-MM-DD, ISO) for Yup. */
const parseDateValue = (value, originalValue) => {
  if (originalValue === null || originalValue === undefined || originalValue === '') {
    return undefined;
  }
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value;
  }
  if (typeof originalValue === 'string') {
    const iso = originalValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      const parsed = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
    const mdy = originalValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (mdy) {
      const parsed = new Date(Number(mdy[3]), Number(mdy[1]) - 1, Number(mdy[2]));
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
  }
  return value;
};

// Validation schema : developers can add their conditional validations here
export default function validationObject(yup, allFields) {
  let obj = {};
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function selectValidation(field) {
    return yup.string().required(`${field.fieldLabel} is required.`);
  }
  
  function textCharactersOnlyValidation(field) {
  const charOnlyRegex = /^[a-zA-Z\s'.-]*$/;
  return yup
    .string()
    .max(150, 'Must be 150 characters or less')
    .min(2, 'Must be 2 characters required')
    .matches(charOnlyRegex, 'Only letters and spaces are allowed')
    .required(`${field.fieldLabel} is required.`);
}

  function textValidation(field) {
     const charOnlyFields = ['PetName'];
  
     if (charOnlyFields.includes(field.fieldName)) {
     return textCharactersOnlyValidation(field);
    }
  
    return yup
      .string()
      .max(150, 'Must be 100 characters or less')
      .min(2, 'Must be 2 characters required')
      .required(`${field.fieldLabel} is required.`);
  }

  function noteValidation(field) {
    return yup
      .string()
      .min(2, 'Must be 2 characters required')
      .required(`${field.fieldLabel} is required.`);
  }

  function multiSelectValidation(field) {
    return yup
      .array()
      .required(`${field.fieldLabel} is required`)
      .min(1, `${field.fieldLabel} is required.`)
      .of(yup.string());
  }

  function multiTagInputValidation(field) {
    return yup
      .array()
      .required(`${field.fieldLabel} is required`)
      .min(1, `${field.fieldLabel} is required.`)
      .of(yup.string());
  }

  function confirmPasswordValidation(field) {
    return yup
      .string()
      .oneOf([yup.ref('password'), null], 'Passwords must match')
      .required(`${field.fieldLabel} is required.`);
  }

  function passwordValidation(field) {
    return yup
      .string()
      .min(5, 'Password must be at least 5 characters')
      .required(`${field.fieldLabel} is required.`);
  }

  function endDateValidation(field) {
    return yup
      .date()
      .required(`${field.fieldLabel} is required.`)
      .typeError('Invalid date, please select the correct date')
      .min(yup.ref('startDate'), 'End date cannot be earlier than start date');
  }

  function startDateValidation(field) {
    return yup
      .date()
      .required(`${field.fieldLabel} is required.`)
      .typeError('Invalid date, please select the correct date')
      .nullable()
      .test('is-valid-date', 'Invalid date format', (value) => {
        if (!value) return true; // Skip validation if empty (handled by required)
        return !isNaN(new Date(value).getTime());
      })
      .test(
        'not-past-date',
        "Date can't be in the past, select future date.",
        (value) => {
          if (!value) return true; // Skip validation if empty
          return !isPastDate(value);
        }
      )
      .transform((value) => {
        return value ? new Date(value) : null;
      });
  }

  function mobileValidation(field) {
    return yup
      .string()
      .min(12, 'Please enter a valid phone number')
      .max(12, 'Please enter a valid phone number')
      .required(`${field.fieldLabel} is required.`);
  }

  function numberValidation(field) {
    // Check if the field label is "EIN"
    if (field.fieldLabel === 'EIN') {
      return yup
        .number()
        .typeError(`${field.fieldLabel} should be a number.`) // Ensure the value is a number
        .min(100000000, `${field.fieldLabel} should be exactly 9 digits long.`) // min 9-digit number
        .max(999999999, `${field.fieldLabel} should be exactly 9 digits long.`) // max 9-digit number
        .required(`${field.fieldLabel} is required.`);
    }

    if (field.fieldLabel === 'Number of Trees to be Removed') {
      return yup
        .number()
        .min(1, `${field.fieldLabel} should be greater than 0`)
        .max(100, `${field.fieldLabel} can't be more than 100`)
        .required(`${field.fieldLabel} is required.`);
    }

    if (field.fieldName === 'lotSize' || field.fieldName === 'PetWeight') {
      return yup
        .number()
        .min(1, `${field.fieldLabel} should be greater than 0`)
        .required(`${field.fieldLabel} is required.`);
    }

    // For other fields, ensure it's a number and required
    return yup.number().required(`${field.fieldLabel} is required.`);
  }

  function emailValidation(field) {
    return yup
      .string()
      .required(`${field.fieldLabel} is required.`)
      .matches(emailRegex, 'Invalid email format.')
      .email();
  }

  function dateValidation(field) {
    return yup
      .date()
      .transform(parseDateValue)
      .required(`${field.fieldLabel} is required.`)
      .typeError('Invalid date, please select the correct date');
  }

  function fileValidation(field) {
    return yup
      .array()
      .required(`${field.fieldLabel} is required`)
      .min(1, `${field.fieldLabel} is required.`);
  }

  function colorPickerValidation(field) {
    return yup.string().required(`${field.fieldLabel} is required.`);
  }

  function autocompleteValidation(field) {
    return yup.string().required(`${field.fieldLabel} is required.`);
  }

  function radioButtonkValidation() {
    return yup.string().required(`This field is required. Select an option.`);
  }

  function checkBoxValidation() {
    return yup
      .boolean()
      .oneOf([true], 'This field is required. Please select this option.');
  }

  function pastDateValidation(field) {
    return yup
      .date()
      .transform(parseDateValue)
      .required(`${field.fieldLabel} is required.`)
      .typeError('Invalid date, please select the correct date');
  }

  function futureDateValidation(field) {
    return yup
      .date()
      .transform(parseDateValue)
      .required(`${field.fieldLabel} is required.`)
      .typeError('Invalid date, please select the correct date');
  }

  allFields?.forEach((field) => {
    if (field?.dependencyFields?.length > 0) {
      field?.dependencyFields?.forEach((dependencyFields) => {
        if (dependencyFields.required) {
          obj[dependencyFields.fieldName] =
            dependencyFields.fieldType === 'date'
              ? dateValidation(dependencyFields)
              : dependencyFields.fieldType === 'email'
              ? emailValidation(dependencyFields)
              : dependencyFields.fieldType === 'number'
              ? numberValidation(dependencyFields)
              : dependencyFields.fieldType === 'mobile'
              ? mobileValidation(dependencyFields)
              : dependencyFields.fieldType === 'startDate'
              ? startDateValidation(dependencyFields)
              : dependencyFields.fieldType === 'endDate'
              ? endDateValidation(dependencyFields)
              : dependencyFields.fieldType === 'pastDate'
              ? pastDateValidation(dependencyFields)
              : dependencyFields.fieldType === 'futureDate'
              ? futureDateValidation(dependencyFields)
              : dependencyFields.fieldType === 'password'
              ? passwordValidation(dependencyFields)
              : dependencyFields.fieldType === 'confirmPassword'
              ? confirmPasswordValidation(dependencyFields)
              : dependencyFields.fieldType === 'text'
              ? textValidation(dependencyFields)
              : dependencyFields.fieldType === 'note'
              ? noteValidation(dependencyFields)
              : dependencyFields.fieldType === 'select'
              ? selectValidation(dependencyFields)
              : dependencyFields.fieldType === 'multiselect'
              ? multiSelectValidation(dependencyFields)
              : dependencyFields.fieldType === 'tagInput'
              ? multiTagInputValidation(dependencyFields)
              : dependencyFields.fieldType === 'file'
              ? fileValidation(dependencyFields)
              : dependencyFields.fieldType === 'colorPicker'
              ? colorPickerValidation(dependencyFields)
              : dependencyFields.fieldType === 'autocomplete'
              ? autocompleteValidation(dependencyFields)
              : dependencyFields.fieldType === 'radio'
              ? radioButtonkValidation(dependencyFields)
              : dependencyFields.fieldType === 'check'
              ? checkBoxValidation(dependencyFields)
              : '';
        }
      });
    }

    if (field.required) {
      obj[field.fieldName] =
        field.fieldType === 'date'
          ? dateValidation(field)
          : field.fieldType === 'email'
          ? emailValidation(field)
          : field.fieldType === 'number'
          ? numberValidation(field)
          : field.fieldType === 'mobile'
          ? mobileValidation(field)
          : field.fieldType === 'startDate'
          ? startDateValidation(field)
          : field.fieldType === 'endDate'
          ? endDateValidation(field)
          : field.fieldType === 'password'
          ? passwordValidation(field)
          : field.fieldType === 'confirmPassword'
          ? confirmPasswordValidation(field)
          : field.fieldType === 'text'
          ? textValidation(field)
          : field.fieldType === 'note'
          ? noteValidation(field)
          : field.fieldType === 'select'
          ? selectValidation(field)
          : field.fieldType === 'multiselect'
          ? multiSelectValidation(field)
          : field.fieldType === 'tagInput'
          ? multiTagInputValidation(field)
          : field.fieldType === 'file'
          ? fileValidation(field)
          : field.fieldType === 'colorPicker'
          ? colorPickerValidation(field)
          : field.fieldType === 'autocomplete'
          ? autocompleteValidation(field)
          : field.fieldType === 'radio'
          ? radioButtonkValidation(field)
          : field.fieldType === 'check'
          ? checkBoxValidation(field)
          : field.fieldType === 'pastDate'
          ? pastDateValidation(field)
          : field.fieldType === 'futureDate'
          ? futureDateValidation(field)
          : '';
    }
  });

  return obj;
}
